import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { useCallback, useEffect } from 'react';
import { BodySmallRegular, Icon, SearchIcon, XStack } from 'smoothie';

import { adminUserAtom } from '../../entities/entityAtoms';
import { AdminUserRole } from '../../entities/entityTypes';
import {
    AllMembershipsGrid,
    MyMembershipsGrid,
} from '../../entities/membership/components/MembershipsList';
import { TrackedEvent } from '../../logger/Events';
import { trackEvent } from '../../logger/Logger';
import { GroveSimpleModal } from './GroveSimpleModal';

export const isQuickSearchModalOpenAtom = atom(false);

export const QuickSearchButton = () => {
    const setIsSearchModalOpen = useSetAtom(isQuickSearchModalOpenAtom);
    const openModal = (openMethod: 'click' | 'keystroke') => {
        setIsSearchModalOpen(true);
        trackEvent(TrackedEvent.QuickSearchModalOpened, { openMethod });
    };
    const closeModal = () => {
        setIsSearchModalOpen(false);
    };
    const openModalOnKeystroke = useCallback((e: KeyboardEvent) => {
        if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
            openModal('keystroke');
        }
    }, []);
    const closeModalOnKeystroke = useCallback((e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            closeModal();
        }
    }, []);
    useEffect(() => {
        document.addEventListener('keydown', openModalOnKeystroke);
        document.addEventListener('keydown', closeModalOnKeystroke);
        return () => {
            document.removeEventListener('keydown', openModalOnKeystroke);
            document.removeEventListener('keydown', closeModalOnKeystroke);
        };
    }, []);

    return (
        <XStack marginRight={16} width="100%" justifyContent="flex-end">
            <XStack
                paddingHorizontal={12}
                paddingVertical={10}
                borderRadius={12}
                borderWidth={1}
                borderColor="$gray200"
                backgroundColor="$gray100"
                width="100%"
                maxWidth="17vw"
                cursor="pointer"
                justifyContent="space-between"
                alignItems="center"
                hoverStyle={{
                    borderColor: '$gray800',
                    backgroundColor: '$white',
                }}
                onPress={() => {
                    openModal('click');
                }}
            >
                <XStack gap={4} alignItems="center">
                    <Icon icon={SearchIcon} size={14} color="$gray1100" />
                    <BodySmallRegular color="$gray800">Search</BodySmallRegular>
                </XStack>
                <BodySmallRegular color="$gray800">⌘ K</BodySmallRegular>
            </XStack>
        </XStack>
    );
};

export const QuickSearchModal = () => {
    const adminUser = useAtomValue(adminUserAtom);
    const [isSearchModalOpen, setIsSearchModalOpen] = useAtom(
        isQuickSearchModalOpenAtom,
    );
    const closeModal = () => {
        setIsSearchModalOpen(false);
    };

    return (
        <GroveSimpleModal
            isOpen={isSearchModalOpen}
            hasCloseButton={true}
            onClose={closeModal}
            renderInAppRoot={true}
        >
            <XStack
                marginTop={48}
                marginHorizontal={16}
                width="90vw"
                maxWidth={1000}
            >
                {adminUser.role === AdminUserRole.GUIDE ? (
                    <MyMembershipsGrid onNavigateFromGrid={closeModal} />
                ) : (
                    <AllMembershipsGrid onNavigateFromGrid={closeModal} />
                )}
            </XStack>
        </GroveSimpleModal>
    );
};
